/**
 * Carousel suggestion / list card sizing. Width matches v1 ~240px cards;
 * height leaves room for the product/page image plus title, price, excerpt
 * (clamped), and optional action footer.
 *
 * Strip size (`small` | `medium` | `large`) shrinks the image band and card
 * height; width stays 240px. Overlay / expand side column ignores strip size.
 */

export const SUGGESTION_STRIP_CARD_WIDTH_PX = 240

/** @deprecated Prefer resolveSuggestionStripSizeTokens('large').imageBandPx */
export const SUGGESTION_CARD_IMAGE_BAND_PX = 220

/** @deprecated Prefer resolveSuggestionStripSizeTokens('large').cardHeightPx */
export const SUGGESTION_STRIP_CARD_HEIGHT_PX = 400

/** @deprecated Prefer resolveSuggestionStripSizeTokens('large').slideOutCardHeightPx */
export const SUGGESTION_SLIDE_OUT_CARD_HEIGHT_PX = 440

/**
 * Overlay / expand right-column cards fill the suggestions pane (see
 * `SuggestionsStrip` `variant="side"`). Image band uses flex ratio, not px.
 */
export const SUGGESTION_SIDE_COLUMN_IMAGE_MIN_PX = 96

/** Shared chrome for suggestion cards (strip + overlay column). */
export const SUGGESTION_CARD_BORDER_CLASS =
  'rounded-xl border border-[var(--color-border)]'

/** Landscape when wider than this (avoids flicker on near-square assets). */
export const SUGGESTION_LANDSCAPE_RATIO_MIN = 1.15

/** @typedef {'small' | 'medium' | 'large'} SuggestionStripSize */

/**
 * @typedef {{
 *   cardHeightPx: number,
 *   slideOutCardHeightPx: number,
 *   imageBandPx: number,
 *   minLandscapeBandPx: number,
 *   titleClass: string,
 *   subtitleClass: string,
 *   subtitleClamp: string,
 *   priceClass: string,
 *   imagePad: string,
 *   bodyPad: string,
 *   bodyGap: string,
 *   ctaTitle: string,
 *   ctaExcerpt: string,
 *   ctaBodyPad: string,
 *   actionTitle: string,
 *   actionExcerpt: string,
 *   actionStackGap: string,
 *   actionTextGap: string,
 *   actionPad: string,
 *   footerBtn: string,
 *   pillBtn: string,
 *   fallbackIcon: string,
 * }} SuggestionStripSizeTokens
 */

/** @type {Record<SuggestionStripSize, SuggestionStripSizeTokens>} */
const STRIP_SIZE_TOKENS = {
  large: {
    cardHeightPx: 400,
    slideOutCardHeightPx: 440,
    imageBandPx: 220,
    minLandscapeBandPx: 96,
    titleClass: 'text-[16px] font-semibold leading-snug',
    subtitleClass: 'text-[13px] leading-snug',
    subtitleClamp: 'line-clamp-2',
    priceClass: 'text-[13px] font-bold',
    imagePad: 'p-2',
    bodyPad: 'px-2.5 py-2',
    bodyGap: 'gap-1.5',
    ctaTitle: 'text-[16px] font-medium leading-snug',
    ctaExcerpt: 'text-[13px] leading-snug',
    ctaBodyPad: 'p-3',
    actionTitle: 'text-[22px] font-semibold leading-snug',
    actionExcerpt: 'text-[16px] leading-relaxed',
    actionStackGap: 'gap-4',
    actionTextGap: 'gap-2.5',
    actionPad: 'px-3 py-4',
    footerBtn: 'min-h-[36px] px-3 py-2 text-[12px]',
    pillBtn: 'min-h-[36px] px-3 py-2 text-[12px]',
    fallbackIcon: 'h-6 w-6 opacity-50',
  },
  medium: {
    cardHeightPx: 340,
    slideOutCardHeightPx: 370,
    imageBandPx: 150,
    minLandscapeBandPx: 84,
    titleClass: 'text-[15px] font-semibold leading-snug',
    subtitleClass: 'text-[12px] leading-snug',
    subtitleClamp: 'line-clamp-2',
    priceClass: 'text-[12px] font-bold',
    imagePad: 'p-1.5',
    bodyPad: 'px-2.5 py-1.5',
    bodyGap: 'gap-1',
    ctaTitle: 'text-[15px] font-medium leading-snug',
    ctaExcerpt: 'text-[12px] leading-snug',
    ctaBodyPad: 'p-2.5',
    actionTitle: 'text-[20px] font-semibold leading-snug',
    actionExcerpt: 'text-[15px] leading-relaxed',
    actionStackGap: 'gap-3',
    actionTextGap: 'gap-2',
    actionPad: 'px-3 py-3',
    footerBtn: 'min-h-[34px] px-3 py-1.5 text-[12px]',
    pillBtn: 'min-h-[34px] px-3 py-1.5 text-[12px]',
    fallbackIcon: 'h-6 w-6 opacity-50',
  },
  small: {
    cardHeightPx: 290,
    slideOutCardHeightPx: 310,
    imageBandPx: 110,
    minLandscapeBandPx: 72,
    titleClass: 'text-[14px] font-semibold leading-snug',
    subtitleClass: 'text-[12px] leading-snug',
    subtitleClamp: 'line-clamp-1',
    priceClass: 'text-[12px] font-bold',
    imagePad: 'p-1.5',
    bodyPad: 'px-2 py-1.5',
    bodyGap: 'gap-1',
    ctaTitle: 'text-[14px] font-medium leading-snug',
    ctaExcerpt: 'text-[12px] leading-snug',
    ctaBodyPad: 'p-2',
    actionTitle: 'text-[18px] font-semibold leading-snug',
    actionExcerpt: 'text-[14px] leading-relaxed',
    actionStackGap: 'gap-2.5',
    actionTextGap: 'gap-1.5',
    actionPad: 'px-2.5 py-2.5',
    footerBtn: 'min-h-[32px] px-2.5 py-1.5 text-[11px]',
    pillBtn: 'min-h-[32px] px-2.5 py-1.5 text-[11px]',
    fallbackIcon: 'h-5 w-5 opacity-50',
  },
}

/**
 * @param {unknown} value
 * @returns {SuggestionStripSize}
 */
export function normalizeSuggestionStripSize(value) {
  const s = typeof value === 'string' ? value.trim().toLowerCase() : ''
  if (s === 'small' || s === 'medium' || s === 'large') return s
  // Unset / unknown → medium (agents that never chose a size).
  return 'medium'
}

/**
 * @param {unknown} size
 * @returns {SuggestionStripSizeTokens}
 */
export function resolveSuggestionStripSizeTokens(size) {
  return STRIP_SIZE_TOKENS[normalizeSuggestionStripSize(size)]
}

/**
 * @param {number} viewportInnerWidthPx
 * @returns {number}
 */
export function clampStandardCardWidthToViewport(viewportInnerWidthPx) {
  const w = Number(viewportInnerWidthPx)
  if (!Number.isFinite(w) || w <= 0) return SUGGESTION_STRIP_CARD_WIDTH_PX
  return Math.min(SUGGESTION_STRIP_CARD_WIDTH_PX, w)
}

/** @typedef {'overlay' | 'slide-out' | 'strip'} SuggestionCardScale */

/**
 * @param {{ sideColumn?: boolean, layoutInline?: boolean, layoutKey?: string | null }} opts
 * @returns {SuggestionCardScale}
 */
export function resolveSuggestionCardScale({
  sideColumn = false,
  layoutInline = false,
  layoutKey = null,
} = {}) {
  if (sideColumn) return 'overlay'
  if (layoutInline && layoutKey === 'slide-out') return 'slide-out'
  return 'strip'
}

/**
 * Fixed card frame height for strip / inline carousel cards (not overlay side column).
 * @param {string | null | undefined} layoutKey
 * @param {unknown} [size]
 * @returns {number}
 */
export function suggestionStripCardHeightPx(layoutKey, size = 'large') {
  const tokens = resolveSuggestionStripSizeTokens(size)
  if (layoutKey === 'slide-out') return tokens.slideOutCardHeightPx
  return tokens.cardHeightPx
}

/**
 * Default image band height for strip / inline cards at the given size.
 * @param {unknown} [size]
 * @returns {number}
 */
export function suggestionStripImageBandPx(size = 'large') {
  return resolveSuggestionStripSizeTokens(size).imageBandPx
}

/**
 * Shorter image band for landscape assets; portrait/square keep defaultBandPx.
 * Card width stays fixed so computed band ≈ rendered image height under object-contain.
 *
 * @param {{
 *   naturalWidth?: number,
 *   naturalHeight?: number,
 *   defaultBandPx: number,
 *   cardWidthPx?: number,
 *   minBandPx?: number,
 * }} opts
 * @returns {number}
 */
export function resolveLandscapeImageBandPx({
  naturalWidth,
  naturalHeight,
  defaultBandPx,
  cardWidthPx = SUGGESTION_STRIP_CARD_WIDTH_PX,
  minBandPx = 72,
} = {}) {
  const defaultBand = Number(defaultBandPx)
  const safeDefault =
    Number.isFinite(defaultBand) && defaultBand > 0 ? defaultBand : SUGGESTION_CARD_IMAGE_BAND_PX
  const w = Number(naturalWidth)
  const h = Number(naturalHeight)
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return safeDefault
  }
  const ratio = w / h
  if (ratio < SUGGESTION_LANDSCAPE_RATIO_MIN) return safeDefault
  const floor =
    Number.isFinite(Number(minBandPx)) && Number(minBandPx) > 0
      ? Number(minBandPx)
      : 72
  const width =
    Number.isFinite(Number(cardWidthPx)) && Number(cardWidthPx) > 0
      ? Number(cardWidthPx)
      : SUGGESTION_STRIP_CARD_WIDTH_PX
  const computed = Math.round(width / ratio)
  return Math.min(safeDefault, Math.max(floor, computed))
}

/**
 * Extra description clamp class when landscape shrink frees body space.
 * @param {string} baseClamp e.g. line-clamp-2
 * @param {boolean} landscapeShrunk
 * @returns {string}
 */
export function suggestionSubtitleClampWithLandscapeBonus(baseClamp, landscapeShrunk) {
  if (!landscapeShrunk || typeof baseClamp !== 'string') return baseClamp || 'line-clamp-2'
  if (baseClamp.includes('line-clamp-1')) return 'line-clamp-2'
  if (baseClamp.includes('line-clamp-2')) return 'line-clamp-3'
  if (baseClamp.includes('line-clamp-3')) return 'line-clamp-4'
  return baseClamp
}

/**
 * Readable type scale per surface. Overlay largest; strip sizes apply to
 * slide-out / strip only (side column ignores `size`).
 *
 * @param {SuggestionCardScale} scale
 * @param {unknown} [size]
 */
export function suggestionCardTypography(scale, size = 'large') {
  if (scale === 'overlay') {
    return {
      imagePad: 'p-3',
      bodyPad: 'px-3 py-2.5',
      bodyGap: 'gap-2',
      title: 'text-[19px] font-semibold leading-snug',
      subtitle: 'text-[15px] leading-snug',
      subtitleClamp: 'line-clamp-3',
      price: 'text-[15px] font-bold',
      ctaTitle: 'text-[18px] font-medium leading-snug',
      ctaExcerpt: 'text-[15px] leading-snug',
      ctaBodyPad: 'p-4',
      actionTitle: 'text-[22px] font-semibold leading-snug',
      actionExcerpt: 'text-[16px] leading-relaxed',
      actionStackGap: 'gap-5',
      actionTextGap: 'gap-3',
      actionPad: 'px-4 py-6',
      footerBtn: 'min-h-[38px] px-4 py-2 text-[13px]',
      pillBtn: 'min-h-[38px] px-4 py-2 text-[13px]',
      fallbackIcon: 'h-16 w-16 opacity-50',
    }
  }

  const tokens = resolveSuggestionStripSizeTokens(size)
  const slideOut = scale === 'slide-out'

  return {
    imagePad: slideOut ? 'p-2.5' : tokens.imagePad,
    bodyPad: tokens.bodyPad,
    bodyGap: tokens.bodyGap,
    title: tokens.titleClass,
    subtitle: tokens.subtitleClass,
    subtitleClamp: tokens.subtitleClamp,
    price: tokens.priceClass,
    ctaTitle: tokens.ctaTitle,
    ctaExcerpt: tokens.ctaExcerpt,
    ctaBodyPad: tokens.ctaBodyPad,
    actionTitle: tokens.actionTitle,
    actionExcerpt: tokens.actionExcerpt,
    actionStackGap: tokens.actionStackGap,
    actionTextGap: tokens.actionTextGap,
    actionPad: tokens.actionPad,
    footerBtn: tokens.footerBtn,
    pillBtn: tokens.pillBtn,
    fallbackIcon: slideOut ? 'h-12 w-12 opacity-50' : tokens.fallbackIcon,
  }
}
